import {useReportUserVisibleError} from '@github-ui/copilot-browser-latency/use-report-user-visible-error'
import {ErrorState} from '@github-ui/copilot-chat/components/ErrorState'
import type {CopilotChatOrg} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {loginPath} from '@github-ui/paths'
import {useLocation} from '@github-ui/react-router'
import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import {use, useEffect, useEffectEvent, useRef} from 'react'

import {ConversationLockedFocusContext, useLapsedSsoOrgLogins} from '../../contexts/ConversationAccessContext'

export const CONVERSATION_LOCKED_TITLE = 'This conversation is protected'
export const CONVERSATION_LOCKED_DESCRIPTION = "You don't currently have access to this conversation."
export const CONVERSATION_LOCKED_SIGN_IN_TITLE = 'Sign in to see this conversation'
export const CONVERSATION_LOCKED_SIGN_IN_DESCRIPTION = 'This conversation is protected.'
export const CONVERSATION_LOCKED_SSO_TITLE = 'Single sign-on required'
export const CONVERSATION_LOCKED_SSO_DESCRIPTION =
  "This conversation is protected. Use your organization's single sign-on to continue."
/** Announced when a lock replaces a conversation that was already on screen. */
export const CONVERSATION_LOCKED_ANNOUNCEMENT = `${CONVERSATION_LOCKED_TITLE}.`

export interface ConversationLockedProps {
  /** Where the conversation was being opened, for analytics. */
  surface: 'task' | 'ahp-timeline' | 'ahp-history' | 'chat'
  /** Re-reads the transcript, e.g. after the user comes back from single sign-on. */
  onRetry?: () => void
  /** SSO organizations, for hosts outside a CopilotChatProvider. */
  ssoOrganizations?: CopilotChatOrg[]
  /** Whether the host knows the viewer must sign in to GitHub before retrying. */
  requiresSignIn?: boolean
}

/**
 * Shown in place of a transcript the viewer may no longer read.
 *
 * Names nothing the conversation reached. When the host knows the viewer is
 * signed out, it offers GitHub sign-in. Otherwise, when the viewer has
 * organizations whose SSO lapsed, it offers single sign-on for them (Chat
 * v1's re-prompt). Unknown refusals leave the generic protected-content lock.
 */
export function ConversationLocked({surface, onRetry, ssoOrganizations, requiresSignIn}: ConversationLockedProps) {
  const ssoOrgs = useLapsedSsoOrgLogins(ssoOrganizations)
  const offeredSso = !requiresSignIn && ssoOrgs.length > 0
  const registerForFocus = use(ConversationLockedFocusContext)
  const location = useLocation()
  const {isBrowser} = useRuntimeEnvironment()
  const title = requiresSignIn
    ? CONVERSATION_LOCKED_SIGN_IN_TITLE
    : offeredSso
      ? CONVERSATION_LOCKED_SSO_TITLE
      : CONVERSATION_LOCKED_TITLE
  const description = requiresSignIn
    ? CONVERSATION_LOCKED_SIGN_IN_DESCRIPTION
    : offeredSso
      ? CONVERSATION_LOCKED_SSO_DESCRIPTION
      : CONVERSATION_LOCKED_DESCRIPTION
  const primaryAction = requiresSignIn
    ? {
        href: loginPath({returnTo: location.pathname + location.search + (isBrowser ? location.hash : '')}),
        label: 'Sign in to GitHub',
        onSelect: () =>
          sendEvent('dotcom_chat.activate', {
            target: 'CONVERSATION_LOCKED_SIGN_IN',
            action: 'selected',
            surface,
          }),
      }
    : undefined

  // One event per lock, and one more if single sign-on is offered. The SSO
  // organizations can arrive after the lock, so folding the offer into the lock
  // event would record the same lock twice. The refs also keep StrictMode's
  // extra effect run from recording either twice.
  const reportedLockRef = useRef(false)
  const reportLock = useEffectEvent(() => {
    if (reportedLockRef.current) return
    reportedLockRef.current = true
    sendEvent('dotcom_chat.activate.error', {
      target: 'ERROR_CONVERSATION_LOCKED',
      message: title,
      surface,
    })
  })
  useEffect(() => reportLock(), [])
  useReportUserVisibleError('access_denied')

  const reportedSsoOfferRef = useRef(false)
  useEffect(() => {
    if (!offeredSso || reportedSsoOfferRef.current) return
    reportedSsoOfferRef.current = true
    sendEvent('dotcom_chat.activate', {target: 'CONVERSATION_LOCKED_SSO_BANNER', action: 'shown', surface})
  }, [offeredSso, surface])

  // Coming back from the identity provider through the back/forward cache
  // restores this page without reloading it, so re-read on the way back.
  const retryOnReturn = useEffectEvent((event: PageTransitionEvent) => {
    if (event.persisted) onRetry?.()
  })
  useEffect(() => {
    window.addEventListener('pageshow', retryOnReturn)
    return () => window.removeEventListener('pageshow', retryOnReturn)
  }, [])

  return (
    <ErrorState
      testId="conversation-locked"
      title={title}
      description={description}
      primaryAction={primaryAction}
      ssoOrgs={offeredSso ? ssoOrgs : undefined}
      focusRef={registerForFocus ?? undefined}
    />
  )
}
